import React, { useEffect, useState } from "react";
import { IAddTestQuestion, ITestQuestionId } from "../../TestQuestion.props";
import { useForm } from "react-hook-form";
import Loader from "../../../../components/Loader";
import useEditTestQuestion from "./useEditTestQuestion";
import { CancelButton, FormButton } from "../../../../components/Botton";

type Field =
  | {
      label: string;
      name: keyof IAddTestQuestion;
      type: "text" | "textarea" | "checkbox";
    }
  | {
      label: string;
      name: keyof IAddTestQuestion;
      type: "select";
      options: any[];
      disabled?: boolean;
    }
  | {
      label: string;
      name: keyof IAddTestQuestion;
      type: "options";
    };

const EditTestQuestion: React.FC<ITestQuestionId> = ({ testQuestionId, setSelectedTestQuestionId }) => {
  const {
    formData,
    courses,
    languages,
    subjectName,
    standard,
    loading,
    handleChange,
    handleSubmit,
    handleCancel,
  } = useEditTestQuestion(testQuestionId, setSelectedTestQuestionId);

  const {
    register,
    handleSubmit: useFormSubmit,
    formState: { errors },
    reset
  } = useForm<IAddTestQuestion>();

  const [selectedStandard, setSelectedStandard] = useState<string>(formData.standard || "");
  const [selectedCourse, setSelectedCourse] = useState<string>(formData.courseName || "");
  const [filteredCourses, setFilteredCourses] = useState(courses || []);
  const [filteredSubjects, setFilteredSubjects] = useState(subjectName || []);

  useEffect(() => {
    if (formData && Object.keys(formData).length > 0) {
      reset(formData);
    }
  }, [formData, reset]);

  useEffect(() => {
    if (formData.standard) setSelectedStandard(formData.standard);
    if (formData.courseName) setSelectedCourse(formData.courseName);
  }, [formData]);

  useEffect(() => {
    if (selectedStandard) {
      const filtered = courses?.filter((course) => course.standard === selectedStandard);
      setFilteredCourses(filtered || []);
    } else {
      setFilteredCourses(courses || []);
    }
  }, [selectedStandard, courses]);

  useEffect(() => {
    if (selectedStandard && selectedCourse) {
      const filtered = subjectName?.filter(
        (subject) =>
          subject.standard === selectedStandard &&
          subject.courseName === selectedCourse
      );
      setFilteredSubjects(filtered || []);
    } else if (selectedStandard) {
      const filtered = subjectName?.filter(
        (subject) => subject.standard === selectedStandard
      );
      setFilteredSubjects(filtered || []);
    } else {
      setFilteredSubjects(subjectName || []);
    }
  }, [selectedStandard, selectedCourse, subjectName]);

  const handleFilteredChange = (e: React.ChangeEvent<HTMLSelectElement>, fieldName: keyof IAddTestQuestion) => {
    const { value } = e.target;

    if (fieldName === 'standard') {
      setSelectedStandard(value);
      handleChange(e, fieldName);
      if (value !== selectedStandard) {
        setSelectedCourse("");
        handleChange({ target: { value: '' } } as React.ChangeEvent<HTMLSelectElement>, 'courseName');
        handleChange({ target: { value: '' } } as React.ChangeEvent<HTMLSelectElement>, 'subject');
      }
    } else if (fieldName === 'courseName') {
      setSelectedCourse(value);
      handleChange(e, fieldName);
      if (value !== selectedCourse) {
        handleChange({ target: { value: '' } } as React.ChangeEvent<HTMLSelectElement>, 'subject');
      }
    } else {
      handleChange(e, fieldName);
    }
  };

  const fields: Field[] = [
    { label: "Question Text", name: "questionText", type: "textarea" },
    { label: "Description", name: "description", type: "textarea" },
    {
      label: "Level",
      name: "level",
      type: "select",
      options: [{ level: "Easy" }, { level: "Medium" }, { level: "Hard" }],
      disabled: false,
    },
    {
      label: "Education",
      name: "standard",
      type: "select",
      options: standard,
      disabled: false,
    },
    {
      label: "Stream",
      name: "courseName",
      type: "select",
      options: filteredCourses,
      disabled: !selectedStandard,
    },
    {
      label: "Subject",
      name: "subject",
      type: "select",
      options: filteredSubjects,
      disabled: !selectedCourse,
    },
    {
      label: "Language",
      name: "languageName",
      type: "select",
      options: languages,
      disabled: false,
    },
    { label: "Is Live Mcq ?", name: "isLiveMcq", type: "checkbox" },
    { label: "Options", name: "options", type: "options" },
  ];

  return (
    <div className="flex flex-col items-center w-full h-screen p-8">
      <div className="bg-white shadow-md rounded-lg pt-6 pb-6 w-full">
        <div className="border-b-2 w-full">
          <h1 className="text-3xl mb-2 text-gray-700 ml-5">Edit Question</h1>
        </div>
        {loading && <Loader />}
        <form onSubmit={useFormSubmit(handleSubmit)} className="space-y-6">
          <fieldset className="m-2 rounded border-opacity-60 bg-white">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6 m-4">
              {fields.map((field) => (
                <div key={field.name}>
                  <label
                    className="block text-gray-700 text-base font-semibold mb-2"
                    htmlFor={field.name}
                  >
                    {field.label}:
                  </label>

                  {field.type === "select" ? (
                    <select
                      id={field.name}
                      {...register(field.name, { required: true })}
                      onChange={(e) => handleFilteredChange(e, field.name)}
                      value={formData[field.name] || ""}
                      disabled={(field as any).disabled}
                      className={`appearance-none border rounded w-full py-2 px-3 text-gray-600 leading-tight focus:outline-none focus:shadow-outline ${
                        (field as any).disabled ? "bg-gray-100" : ""
                      }`}
                    >
                      <option value="">Select {field.label}</option>
                      {(field as any)?.options?.map((option: any) => (
                        <option key={option._id || option[field.name]} value={option[field.name]}>
                          {option[field.name]}
                        </option>
                      ))}
                    </select>
                  ) : field.type === "checkbox" ? (
                    <input
                      type="checkbox"
                      {...register(field.name)}
                      checked={formData[field.name] || false}
                      onChange={(e) => handleChange(e, field.name)}
                      className="w-4 h-4 text-blue-600 bg-gray-100 border-gray-300 rounded focus:ring-blue-500"
                    />
                  ) : field.type === "options" ? (
                    <div>
                      {formData.options.map((option: any, index: number) => (
                        <div key={index} className="mb-2">
                          <textarea
                            className="appearance-none border rounded w-full py-2 px-3 text-gray-600 leading-tight focus:outline-none focus:shadow-outline"
                            placeholder={`Option ${index + 1}`}
                            rows={2}
                            value={option.optionText}
                            onChange={(e) =>
                              handleChange(e, `options[${index}].optionText`)
                            }
                          />
                          <label className="inline-flex items-center mt-2 mb-5">
                            <input
                              type="checkbox"
                              className="form-checkbox h-5 w-5 text-gray-600"
                              checked={option.isCorrect}
                              onChange={(e) =>
                                handleChange(e, `options[${index}].isCorrect`)
                              }
                            />
                            <span className="ml-2 text-gray-700">Correct</span>
                          </label>
                        </div>
                      ))}
                    </div>
                  ) : field.type === "textarea" ? (
                    <textarea
                      className="appearance-none border rounded w-full py-2 px-3 text-gray-600 leading-tight focus:outline-none focus:shadow-outline"
                      rows={4}
                      {...register(field.name, { required: true })}
                      id={field.name}
                      value={formData[field.name] || ""}
                      onChange={(e) => handleChange(e, field.name)}
                    />
                  ) : (
                    <input
                      className="appearance-none border rounded w-full py-2 px-3 text-gray-600 leading-tight focus:outline-none focus:shadow-outline"
                      type="text"
                      {...register(field.name, { required: true })}
                      id={field.name}
                      value={formData[field.name] || ""}
                      onChange={(e) => handleChange(e, field.name)}
                    />
                  )}

                  {(errors as any)[field.name] && (
                    <span className="text-red-500 text-xs italic">
                      {field.label} field is required
                    </span>
                  )}
                </div>
              ))}
            </div>
          </fieldset>
          <div className="flex justify-start mt-6 ml-10">
            <FormButton className="py-2 px-4 rounded mr-4" type="submit">
              Update
            </FormButton>
            <CancelButton
              className="py-2 px-4 rounded"
              onClick={handleCancel}
              type="button"
            >
              Cancel
            </CancelButton>
          </div>
        </form>
      </div>
    </div>
  );
};

export default EditTestQuestion;
